import { contentRoleCan, isDataClientError, type ContentAct, type ContentTeamRole } from '@kk/domain';
import { useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from 'react';

import { localSessionStorage } from '@/auth/localSessionStorage';
import { createStaffClients, type StaffClients } from '@/data/client';
import { apiBaseUrl } from '@/lib/env';

import { staffSessionIsLive, type SignInProblem, type StaffSession } from './staffSession';

export type SessionStatus = 'loading' | 'signedOut' | 'signedIn';

export interface SessionState {
  status: SessionStatus;
  session: StaffSession | null;
  /**
   * Why there is no session, when it is not simply "signed out". The sign-in screen draws it; nothing
   * else reads it. Cleared by a session arriving.
   */
  problem: SignInProblem | null;
  /** The organization the shell is scoped to: the first membership unless the picker changed it. */
  orgId: string | null;
  setOrgId: (orgId: string) => void;
}

export const SessionContext = createContext<SessionState>({
  status: 'loading',
  session: null,
  problem: null,
  orgId: null,
  setOrgId: () => {},
});

export const ClientsContext = createContext<StaffClients | null>(null);

interface SessionHolder {
  session: StaffSession | null;
  /** Asks the auth client for the session again — which refreshes an access token that has run out. */
  renew?: () => Promise<StaffSession | null>;
}

/**
 * Owns the staff session (the mobile holder pattern): reads storage once at boot, follows auth.onChange, and feeds
 * the token provider through a mutable holder so every request carries the live token or none (→ UNAUTHORIZED
 * without a round trip). Returning to the tab re-reads storage so an expiry is announced. Identity changes clear
 * the query cache.
 */
export function SessionProvider({
  children,
  clients: injected,
}: PropsWithChildren<{ clients?: StaffClients }>) {
  const queryClient = useQueryClient();
  const [state, setState] = useState<{
    status: SessionStatus;
    session: StaffSession | null;
    problem: SignInProblem | null;
  }>({
    status: 'loading',
    session: null,
    problem: null,
  });
  const [pickedOrg, setPickedOrg] = useState<string | null>(null);
  const [{ holder, clients }] = useState<{ holder: SessionHolder; clients: StaffClients }>(() => {
    const h: SessionHolder = { session: null };
    const created =
      injected ??
      createStaffClients({
        baseUrl: apiBaseUrl(),
        sessionStorage: localSessionStorage(),
        getAccessToken: async () => {
          if (h.session && staffSessionIsLive(h.session, new Date(Date.now() + 60_000))) return h.session.accessToken;
          // our own sessions last an hour a token: renew quietly rather than sign somebody out mid-task
          if (h.session && h.renew) return (await h.renew().catch(() => null))?.accessToken ?? null;
          return h.session && staffSessionIsLive(h.session, new Date()) ? h.session.accessToken : null;
        },
      });
    h.renew = () => created.auth.getSession();
    return { holder: h, clients: created };
  });

  useEffect(() => {
    let cancelled = false;
    const apply = (session: StaffSession | null, problem: SignInProblem | null = null) => {
      holder.session = session;
      setState({
        status: session ? 'signedIn' : 'signedOut',
        session,
        problem: session ? null : problem,
      });
    };
    /*
     * A failure here is not always a sign-out. INVALID_CREDENTIALS is Auth0 saying yes and this API
     * saying no — a parent at the club portal — and the screen has a sentence for exactly that. It
     * used to be swallowed, so the only symptom was the sign-in button doing nothing (Evan,
     * 2026-09-16: the two silent catches).
     */
    const problemOf = (e: unknown): SignInProblem => {
      const problem: SignInProblem =
        isDataClientError(e) && e.code === 'INVALID_CREDENTIALS'
          ? 'notStaff'
          : isDataClientError(e) && e.code === 'FORBIDDEN' && reasonOf(e) === 'notCompanyAccount'
            ? 'notCompanyAccount'
            : 'failed';
      /*
       * A caught exception that becomes a user-facing card must say what it caught (Evan, 2026-09-16).
       * The "We couldn't sign you in" card rendered in production with an EMPTY console, so the only
       * evidence of what threw was which network request never happened. The refusal of a non-staff
       * account is an answer rather than a fault, so it is logged quietly; everything else is an error.
       */
      if (problem === 'failed') console.error('[kk portal] sign-in failed:', e);
      else if (problem === 'notCompanyAccount') console.info('[kk portal] a personal Google account where a company one was asked for');
      else console.info('[kk portal] signed in, but not staff of any organization');
      return problem;
    };
    const readStored = () =>
      clients.auth.getSession().then(
        (s) => {
          if (!cancelled) apply(s);
        },
        (e: unknown) => {
          if (!cancelled) apply(null, problemOf(e));
        },
      );
    /*
     * An Auth0 sign-in comes back to this origin carrying ?code and ?state, so the callback is handled
     * at boot rather than on a route of its own — the redirect URI is the app root, which is also what
     * Auth0 is configured with. completeRedirect returns null when there is nothing to complete, so
     * this is the same code path on an ordinary load, and it strips the code out of the address bar
     * either way so a refresh cannot replay it.
     */
    const boot = clients.auth.completeRedirect
      ? clients.auth.completeRedirect().then(
          (s) => (s ? apply(s) : readStored()),
          // the callback itself failed — a code that would not exchange, or an account we refuse.
          // Read storage in case there is still a session, but keep the reason: it is the only
          // thing anyone can act on, and dropping it left a blank screen behind a green button.
          (e: unknown) => {
            if (!cancelled) apply(null, problemOf(e));
          },
        )
      : readStored();
    void boot;
    const off = clients.auth.onChange((s) => {
      const before = holder.session?.user.sub ?? null;
      apply(s);
      if (before !== (s?.user.sub ?? null)) {
        queryClient.clear();
        setPickedOrg(null);
      }
    });
    const onVisible = () => {
      if (document.visibilityState === 'visible' && holder.session) void readStored();
    };
    document.addEventListener('visibilitychange', onVisible);
    // UNAUTHORIZED from any query or mutation (a rotated key, a revoked token, a locally expired session) signs
    // the user out — centrally, so no screen has to watch for it. Deferred out of the cache's notify loop.
    const unauthorized = (e: unknown) => isDataClientError(e) && e.code === 'UNAUTHORIZED';
    const signOutOnce = () => {
      if (!holder.session) return;
      holder.session = null;
      queueMicrotask(() => void clients.auth.signOut());
    };
    const offQueries = queryClient.getQueryCache().subscribe((ev) => {
      if (ev.type === 'updated' && ev.action.type === 'error' && unauthorized(ev.action.error))
        signOutOnce();
    });
    const offMutations = queryClient.getMutationCache().subscribe((ev) => {
      if (ev.type === 'updated' && ev.action.type === 'error' && unauthorized(ev.action.error))
        signOutOnce();
    });
    return () => {
      cancelled = true;
      off();
      offQueries();
      offMutations();
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [clients, holder, queryClient]);

  const value = useMemo<SessionState>(() => {
    const memberships = state.session?.memberships ?? [];
    const orgId =
      pickedOrg && memberships.some((m) => m.orgId === pickedOrg)
        ? pickedOrg
        : (memberships[0]?.orgId ?? null);
    return { ...state, orgId, setOrgId: setPickedOrg };
  }, [state, pickedOrg]);

  return (
    <SessionContext.Provider value={value}>
      <ClientsContext.Provider value={clients}>{children}</ClientsContext.Provider>
    </SessionContext.Provider>
  );
}

/** A refusal's `details.reason`, when it carries one. */
function reasonOf(e: { details?: unknown }): unknown {
  return typeof e.details === 'object' && e.details !== null ? (e.details as { reason?: unknown }).reason : undefined;
}

export function useSession(): SessionState {
  return useContext(SessionContext);
}

export function useClients(): StaffClients {
  const c = useContext(ClientsContext);
  if (!c) throw new Error('useClients outside SessionProvider');
  return c;
}

/**
 * **Kiddie Kredit's own staff, READ from the session rather than inferred from a route refusing** (M-41).
 *
 * `StaffMe.superAdmin` is absent for everyone else rather than false, so the word never reaches a club's
 * portal. A super admin may hold NO membership at all — the bootstrap list needs no account row — which is
 * why every gate below asks this before it asks for an `orgId` (preconditions Corollary A-5).
 */
export function useSuperAdmin(): boolean {
  // the content team (the Team page, 2026-09-26): a permanent owner, or anyone with a role on the team
  const session = useSession().session;
  return session?.superAdmin === true || !!session?.contentRole;
}

/** The caller's role on the content team: an owner sees the Team page's add and remove controls. */
export function useContentRole(): ContentTeamRole | null {
  const session = useSession().session;
  if (session?.contentRole) return session.contentRole;
  return session?.superAdmin === true ? 'owner' : null;
}

/**
 * **What the caller's role may do** (`@kk/domain` rules/contentTeam.ts — the API's own table). A control the role cannot
 * use is not drawn; the API refuses it either way.
 */
export function useContentCan(act: ContentAct): boolean {
  const role = useContentRole();
  return role !== null && contentRoleCan(role, act);
}

/** The membership the shell is scoped to (null while signed out). */
export function useMembership() {
  const { session, orgId } = useSession();
  return session?.memberships.find((m) => m.orgId === orgId) ?? null;
}
